import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { Button, Card, CardBody, Col, Container, Row, Spinner, Table, Badge, InputGroup, InputGroupText, Input, Modal, ModalHeader, Label, ModalBody, FormGroup, ModalFooter } from "reactstrap";
import { FaCubes, FaEdit, FaExchangeAlt, FaFileExcel, FaFileUpload, FaSearch, FaTimes, FaWarehouse } from "react-icons/fa";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { stockActions } from "../../sagas/stockSlice";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { CBL_PAGE, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, PAY_PAGE, POS_PAGE, RETOUR_FORM_NEW, RETOUR_PAGE } from "../../routes/routeConstants";
import POSHeader from "./POSHeader";
import { useNavigate } from "react-router-dom";
import POSKeypadModal from "./POSKeypadModal";
import "./Stocks.css";
import POSNumericKeypadModal from "./POSNumericKeypadModal";

const EntrepotCard = ({ entrepot, active, onClick }) => {
    return (
        <div
            className={`pos-entrepot-card ${active ? "active" : ""}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
        >
            <div className="icon">
                <FaWarehouse />
            </div>

            <div className="label">
                {entrepot.label ?? `${entrepot.libelle}`}
            </div>

            {active && <Badge color="primary" pill className="badge">✔</Badge>}
        </div>
    );
};


const CStockPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { stocks, loading: stockLoading, error } = useSelector((state) => state.stock);
    const { entrepots, loading: entrepotLoading } = useSelector((state) => state.entrepots);
    const fileInputRef = useRef(null);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEntrepot, setSelectedEntrepot] = useState(null);
    const [activeTab, setActiveTab] = useState("STOCK");
    const navigate = useNavigate();
    const { role, businessName, commercialId, pointOfSaleId, fullName } = useSelector(state => state.auth);
    const isAdmin = role === 'ROLE_ADMIN';
    const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
    const [destEntrepotModal, setDestEntrepotModal] = useState(false);
    const [qtyKeypadOpen, setQtyKeypadOpen] = useState(false);
    const [qtyKeypadValue, setQtyKeypadValue] = useState("");
    const [editStockKeypad, setEditStockKeypad] = useState({
        open: false,
        articleId: null,
        entrepotId: null,
        articleNom: "",
        entrepotNom: "",
        value: "",
    });


    const [transferModal, setTransferModal] = useState(false);
    const [transfer, setTransfer] = useState({
        articleId: null,
        articleNom: "",
        entrepotSrcId: null,
        entrepotSrcNom: "",
        entrepotDestId: null,
        quantite: "",
        maxQuantite: 0,
    });

    const toggleTransferModal = () => setTransferModal((v) => !v);


    // Fetch stocks whenever selection changes
    useEffect(() => {
        dispatch(entrepotActions.list());
    }, []);


    useEffect(() => {
        if (selectedEntrepot) {
            dispatch(stockActions.listStocks({ entrepotId: selectedEntrepot?.value }));
        }
    }, [selectedEntrepot])

    useEffect(() => {
        const id = setTimeout(() => setSearchTerm(searchInput.trim()), 200);
        return () => clearTimeout(id);
    }, [searchInput]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "F2") {
                e.preventDefault();
                setSearchKeypadOpen(true);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);


    const entrepotOptions = useMemo(
        () =>
            (entrepots ?? []).map((entrepot) => ({
                value: entrepot.id,
                label: `${entrepot.code} - ${entrepot.libelle}`,
                raw: entrepot,
            })),
        [entrepots]
    );

    const allOption = { value: null, label: t("common.allWarehouses") || "Tous les entrepôts" };

    // simple case-insensitive contains
    const matches = useCallback((value, q) => {
        if (!q) return true;
        if (!value) return false;
        return String(value).toLowerCase().includes(q.toLowerCase());
    }, []);

    const refrechStocks = () => {
        dispatch(stockActions.listStocks({
            entrepotId: selectedEntrepot ? selectedEntrepot.value : null,
        }))
    }

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const allowedTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: t('product.invalidFileType'),
                    text: t('product.excelOnly'),
                });
                return;
            }

            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('entrepotId', selectedEntrepot?.value);

                let payload = {
                    formData: formData,
                    onSuccess: () => {
                        refrechStocks();
                        Swal.fire({
                            icon: 'success',
                            title: t('stock.uploadSuccess'),
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#198754'
                        });
                    },
                }
                dispatch(stockActions.uploadStocks(payload));

            } catch (error) {
                console.error('Error parsing Excel:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur!',
                    text: "Erreur lors de l'importation des produits",
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            } finally {
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const filteredStocks = useMemo(() => {
        if (!stocks) return [];
        if (!searchTerm) return stocks;
        return stocks.filter((s) => {
            return (
                matches(s.articleNom, searchTerm) ||
                matches(s.entrepotNom, searchTerm) || matches(s.articleCode, searchTerm)
                || matches(s.articleCodeBarre, searchTerm)
                // add more fields if needed:
                // || matches(s.articleCode, searchTerm)
            );
        });
    }, [stocks, searchTerm, matches]);

    const handleExportExcel = useCallback(() => {
        const now = new Date();
        const datePart = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");

        // En-têtes localisés
        const HEAD_ENTREPOT = t("entrepot.libelle") || "Entrepôt";
        const HEAD_ARTICLE = t("product.description") || "Article";
        const HEAD_ARTICLE_ID = "Article ID";
        const HEAD_QTE = t("product.stockDisponible") || "Stock disponible";
        console.log(filteredStocks)
        // Transformer les données visibles (filteredStocks) en lignes simples
        const data = filteredStocks.map((s) => ({
            [HEAD_ENTREPOT]: s.entrepotNom ?? "",
            [HEAD_ARTICLE_ID]: s.articleId ?? "",
            [HEAD_ARTICLE]: s.articleNom ?? "",
            [HEAD_QTE]: s.quantiteDisponible ?? 0,
        }));

        // Créer feuille & classeur
        const ws = XLSX.utils.json_to_sheet(data, { skipHeader: false });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stocks");

        // Auto-width colonnes (basé sur la longueur max des cellules)
        const headers = [HEAD_ENTREPOT, HEAD_ARTICLE_ID, HEAD_ARTICLE, HEAD_QTE];
        const colWidths = headers.map((h) => ({ wch: Math.max(h.length, 12) }));
        data.forEach((row) => {
            headers.forEach((h, idx) => {
                const cellLen = String(row[h] ?? "").length;
                if (cellLen + 2 > (colWidths[idx].wch || 10)) colWidths[idx].wch = cellLen + 2;
            });
        });
        ws["!cols"] = colWidths;

        // Nom de fichier contextualisé par entrepôt + date
        const entrepotLabel =
            selectedEntrepot?.label ||
            allOption.label ||
            "tous-entrepots";
        const safeLabel = String(entrepotLabel).toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
        const filename = `stocks_${safeLabel}_${datePart}.xlsx`;

        // Écrire et déclencher le téléchargement
        XLSX.writeFile(wb, filename);
    }, [filteredStocks, selectedEntrepot, t]);

    const handleClickCard = useCallback(
        (id) => {
            if (id === null) {
                setSelectedEntrepot(null);
            } else {
                const found = entrepotOptions.find((o) => o.value === id);
                setSelectedEntrepot(found ? { value: found.value, label: found.label } : null);
            }
        },
        [entrepotOptions]
    );

    const loading = stockLoading || entrepotLoading;

    const totalLines = filteredStocks?.length || 0;

    const totalQuantity = useMemo(() => {
        return (filteredStocks || []).reduce(
            (sum, s) => sum + (Number(s.quantiteDisponible) || 0),
            0
        );
    }, [filteredStocks]);

    // -------- TRANSFER FEATURE ----------
    const openTransferModal = (stock) => {
        // prefill source from row
        setTransfer({
            articleId: stock.articleId,
            articleNom: stock.articleNom,
            entrepotSrcId: stock.entrepotId ?? selectedEntrepot?.value,
            entrepotSrcNom: stock.entrepotNom,
            entrepotDestId: null,
            quantite: "",
            maxQuantite: Number(stock.quantiteDisponible ?? 0),
        });
        setTransferModal(true);
    };

    const handleAddTransfer = (articleId, entrepotSrcId, entrepotDestId, quantite) => {
        Swal.fire({
            title: t("stock.confirmationTransfer") || "Confirmation de transfert",
            text: t("stock.saveOrConfirmTransferText") || "Voulez-vous confirmer ce transfert ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t("common.save") || "Valider",
            cancelButtonText: t("common.cancel") || "Annuler",
        }).then((result) => {
            if (result.isConfirmed) {
                // ✅ compat: send both keys (quantite + quantiteTransfered)
                let payload = {
                    data: {
                        articleId,
                        entrepotSrcId,
                        entrepotDestId,
                        quantite,
                        quantiteTransfered: quantite,
                    },
                    onSuccess: () => {
                        Swal.fire({
                            icon: "success",
                            title: t("common.updated") || "Mis à jour",
                            text: t("stock.transferSuccess") || "Transfert effectué avec succès",
                        });

                        refrechStocks();
                        setTransferModal(false);
                    },
                };
                dispatch(stockActions.transferStock(payload));
            }
        });
    };

    const submitTransfer = () => {
        const qte = Number(transfer.quantite);

        if (!transfer.entrepotSrcId) {
            Swal.fire({ icon: "warning", title: "Entrepôt source", text: "Veuillez sélectionner un entrepôt source." });
            return;
        }

        if (!transfer.entrepotDestId) {
            Swal.fire({ icon: "warning", title: "Entrepôt destination", text: "Veuillez sélectionner un entrepôt destination." });
            return;
        }

        if (transfer.entrepotSrcId === transfer.entrepotDestId) {
            Swal.fire({
                icon: "warning",
                title: "Transfert invalide",
                text: "Entrepôt source et destination doivent être différents.",
            });
            return;
        }

        if (!transfer.quantite || isNaN(qte) || qte <= 0) {
            Swal.fire({ icon: "warning", title: "Quantité", text: "Veuillez saisir une quantité valide > 0." });
            return;
        }

        if (qte > Number(transfer.maxQuantite || 0)) {
            Swal.fire({
                icon: "warning",
                title: "Quantité invalide",
                text: `La quantité ne doit pas dépasser le stock disponible (${transfer.maxQuantite}).`,
            });
            return;
        }

        handleAddTransfer(transfer.articleId, transfer.entrepotSrcId, transfer.entrepotDestId, qte);
    };

    const handleUpdateQte = (articleId, entrepotId, qteEnStock) => {
        Swal.fire({
            title: t("stock.confirmation"),
            text: t("stock.saveOrConfirmText"),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t("common.save"),
            cancelButtonText: t("common.cancel"),
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    data: { articleId, entrepotId, quantiteEnStock: qteEnStock },
                    onSuccess: () => {
                        Swal.fire({
                            icon: "success",
                            title: t("common.updated"),
                            text: t("stock.updatedSuccess"),
                        });

                        refrechStocks();
                    },
                };
                dispatch(stockActions.updateStock(payload));
            }
        });
    };

    return (
        <>
            {loading ? (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="mt-2 text-muted">{t("common.loading")}</div>
                </div>
            ) : (
                <Container fluid>
                    <POSHeader
                        businessName={businessName || "MATJARPOS"}
                        fullName={fullName}
                        commercialId={commercialId}
                        pointOfSaleId={pointOfSaleId}
                        isAdmin={isAdmin}
                        activeTab={activeTab}
                        onChangeTab={(tab) => {
                            console.log(tab)
                            setActiveTab(tab);
                            if (tab === "VENTES") navigate(POS_PAGE);
                            if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
                            if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
                            if (tab === "AVANCE") navigate(PAY_PAGE);
                            if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
                            if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
                        }}
                    />

                    {/* Header */}
                    {/* TOP POS TOOLBAR : Entrepôts (gauche) + Search/KPIs (droite) */}
                    <Row className="g-2 mb-2">
                        <Col lg={8} md={7} className="pe-lg-1">
                            <div className="pos-entrepots-strip">
                                {(entrepots.filter(e => e.libelle !== 'EXTERNE' && e.libelle !== 'RETOUR') ?? []).map(e => (
                                    <EntrepotCard
                                        key={e.id}
                                        entrepot={e}
                                        active={selectedEntrepot?.value === e.id}
                                        onClick={() => handleClickCard(e.id)}
                                    />
                                ))}
                            </div>
                        </Col>

                        <Col lg={4} md={5} className="ps-lg-1">
                            <div className="pos-right-tools">
                                {/* Search */}
                                <div className="pos-search-bar" onClick={() => setSearchKeypadOpen(true)}>
                                    <FaSearch className="pos-search-icon" />

                                    <Input
                                        type="text"
                                        value={searchInput}
                                        readOnly
                                        inputMode="none"
                                        onKeyDown={(e) => e.preventDefault()}
                                        placeholder={t("common.search")}
                                        className="pos-search-input pos-nokeyboard"
                                    />

                                    {searchInput ? (
                                        <Button
                                            color="light"
                                            className="pos-search-clear"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSearchInput("");
                                            }}
                                        >
                                            <FaTimes />
                                        </Button>
                                    ) : (
                                        <div className="pos-search-hint">F2</div>
                                    )}
                                </div>

                                {/* KPIs compact */}
                                <div className="pos-kpis-compact">
                                    <div className="pos-kpi-mini">
                                        <small>{t("stock.totalLines") || "Articles"}</small>
                                        <strong>{totalLines}</strong>
                                    </div>


                                </div>
                            </div>
                        </Col>
                    </Row>



                    <Row>
                        <div className="pos-table-wrap">
                            <Table bordered hover className="pos-table align-middle">
                                <thead className="sticky-top">
                                    <tr>
                                        <th>{t("entrepot.libelle")}</th>
                                        <th style={{ width: 140 }}>{t("product.code")}</th>
                                        <th style={{ width: 160 }}>{t("product.barcode")}</th>
                                        <th>{t("product.description")}</th>
                                        <th style={{ width: 120 }} className="text-end">
                                            {t("product.stockDisponible")}
                                        </th>
                                        {fullName?.includes('AZIZ') && <th style={{ width: "140px" }}>{t("common.actions") || "Actions"}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {!selectedEntrepot || !filteredStocks || filteredStocks.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="text-muted">{t("stock.noStocks")}</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredStocks.map((stock) => (
                                            <tr key={stock.id}>
                                                <td>{stock.entrepotNom}</td>
                                                <td>{stock.articleCode}</td>
                                                <td>{stock.articleCodeBarre}</td>
                                                <td>{stock.articleNom}</td>
                                                <td className="text-end fw-semibold">
                                                    <span>{stock.quantiteDisponible}</span>

                                                    {fullName?.includes('AZIZ') && <Button
                                                        size="sm"
                                                        color="light"
                                                        className="border shadow-sm"
                                                        title={t("common.edit") || "Modifier"}
                                                        onClick={() => {
                                                            setEditStockKeypad({
                                                                open: true,
                                                                articleId: stock.articleId,
                                                                entrepotId: stock.entrepotId ?? selectedEntrepot?.value,
                                                                articleNom: stock.articleNom,
                                                                entrepotNom: stock.entrepotNom,
                                                                value: stock.quantiteDisponible?.toString() || "0"
                                                            });
                                                        }}
                                                    >
                                                        <FaEdit />
                                                    </Button>}
                                                </td>
                                                {fullName?.includes('AZIZ') && <td className="d-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        color="warning"
                                                        outline
                                                        className="border shadow-sm"
                                                        title={t("stock.transfer") || "Transférer"}
                                                        onClick={() => openTransferModal(stock)}
                                                        disabled={!stock.articleId || !stock.entrepotId}
                                                    >
                                                        <FaExchangeAlt className="me-1" />
                                                        {t("stock.transfer") || "Transférer"}
                                                    </Button>
                                                </td>}
                                            </tr>
                                        ))
                                    )}
                                </tbody>

                            </Table>
                        </div>
                    </Row>
                </Container>
            )}

            <Modal isOpen={transferModal} toggle={toggleTransferModal} centered>
                <ModalHeader toggle={toggleTransferModal}>{t("stock.transferTitle") || "Transfert de stock"}</ModalHeader>

                <ModalBody>
                    <div className="mb-3">
                        <Label className="text-muted">{t("stock.article") || "Article"}</Label>
                        <div className="fw-semibold">{transfer.articleNom || "-"}</div>
                    </div>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>{t("stock.entrepotSource") || "Entrepôt source"}</Label>
                                <Input value={transfer.entrepotSrcNom || ""} disabled />
                            </FormGroup>
                        </Col>

                        <Col md={12}>
                            <FormGroup>
                                <div
                                    className="pos-field"
                                    onClick={() => setDestEntrepotModal(true)}
                                >
                                    <div className="pos-field-label">
                                        {t("stock.entrepotDest") || "Entrepôt destination"}
                                    </div>
                                    <div className="pos-field-value">
                                        {
                                            entrepots.find(e => e.id === transfer.entrepotDestId)?.libelle
                                            || "Touch to select"
                                        }
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>

                        <Col md={12}>
                            <FormGroup>
                                <div
                                    className="pos-field"
                                    onClick={() => {
                                        setQtyKeypadValue(transfer.quantite?.toString() || "");
                                        setQtyKeypadOpen(true);
                                    }}
                                >
                                    <div className="pos-field-label">
                                        {t("stock.transferQty") || "Quantité à transférer"}
                                        <span className="text-muted ms-2">
                                            (Dispo: {transfer.maxQuantite})
                                        </span>
                                    </div>
                                    <div className="pos-field-value">
                                        {transfer.quantite || "Touch to enter"}
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <div className="small text-muted">
                        {t("stock.transferHint") ||
                            "Le transfert va décrémenter le stock de l’entrepôt source et incrémenter celui de l’entrepôt destination, avec création d’un mouvement TRANSFERT."}
                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={toggleTransferModal}>
                        {t("common.cancel") || "Annuler"}
                    </Button>
                    <Button color="primary" onClick={submitTransfer} disabled={stockLoading}>
                        {t("common.save") || "Valider"}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={destEntrepotModal} toggle={() => setDestEntrepotModal(false)}>
                <ModalHeader>Choisir Entrepôt Destination</ModalHeader>
                <ModalBody>
                    {entrepots
                        .filter(e => e.id !== transfer.entrepotSrcId)
                        .map(e => (
                            <Button
                                key={e.id}
                                block
                                color="light"
                                className="pos-select-btn mb-2"
                                onClick={() => {
                                    setTransfer(s => ({ ...s, entrepotDestId: e.id }));
                                    setDestEntrepotModal(false);
                                }}
                            >
                                {e.code} - {e.libelle}
                            </Button>
                        ))}
                </ModalBody>
            </Modal>
            <POSKeypadModal
                isOpen={searchKeypadOpen}
                toggle={() => setSearchKeypadOpen(false)}
                value={searchInput}
                onChange={(val) => {
                    setSearchInput(val || "");
                }}
                onSearch={() => {
                    setSearchKeypadOpen(false);
                }}
                title={`Recherche Stock`}
            />
            <POSNumericKeypadModal
                isOpen={qtyKeypadOpen}
                toggle={() => setQtyKeypadOpen(false)}
                value={qtyKeypadValue}
                onChange={setQtyKeypadValue}
                onConfirm={(val) => {
                    setTransfer(s => ({ ...s, quantite: val }));
                    setQtyKeypadOpen(false);
                }}
                title="Quantité à transférer"
            />

            <POSNumericKeypadModal
                isOpen={editStockKeypad.open}
                toggle={() => setEditStockKeypad(s => ({ ...s, open: false }))}
                value={editStockKeypad.value}
                onChange={(val) => setEditStockKeypad(s => ({ ...s, value: val }))}
                onConfirm={(val) => {
                    const newQte = Number(val);

                    if (isNaN(newQte) || newQte < 0) {
                        Swal.fire("Erreur", "Quantité invalide", "error");
                        return;
                    }

                    handleUpdateQte(editStockKeypad.articleId, editStockKeypad.entrepotId, newQte);
                    setEditStockKeypad(s => ({ ...s, open: false }));
                }}
                title={`Modifier stock — ${editStockKeypad.articleNom}`}
            />


        </>
    );
};

export default CStockPage;
